import { type IDiscountRedemption } from '@mahawi/eshop-common/src/types';
import { Divider, Drawer, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { EDateFormat } from '../../fragments/date-format';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type RootState } from '../../reducers';
import { discountRedemptionsList } from '../../reducers/discount/reducer';
import { type IDiscountAssignmentAdmin } from '../../reducers/discount/types';
import Link from '../link';

const { Text } = Typography;

function DiscountRedemptionsDrawer({
  open,
  onClose,
  discountUUID,
  discountCode,
}: {
  open: boolean;
  onClose: () => void;
  discountUUID: string | null;
  discountCode: string | null;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const redemptions = useSelector((s: RootState) => s.Discount.redemptions);
  const assignments = useSelector((s: RootState) => s.Discount.assignments);
  const inProcess = useSelector((s: RootState) => s.Discount.inProcess);

  useEffect(() => {
    if (open && discountUUID) {
      dispatch(discountRedemptionsList(discountUUID));
    }
  }, [open, discountUUID, dispatch]);

  return (
    <Drawer
      title={`Usage${discountCode ? ` — ${discountCode}` : ''}`}
      open={open}
      onClose={onClose}
      size={480}
    >
      <Table
        rowKey={(record: IDiscountRedemption): string =>
          `${record.order.uuid}-${record.createdAt}`
        }
        loading={inProcess}
        dataSource={redemptions ?? []}
        pagination={false}
        columns={[
          {
            title: 'Order',
            dataIndex: ['order', 'uuid'],
            render: (uuid: string): React.ReactElement => (
              <Link to={`/order/${uuid}`} label={uuid} openInNewTab />
            ),
          },
          {
            title: 'Customer',
            dataIndex: 'customer',
            render: (
              customer: IDiscountRedemption['customer'],
            ): React.ReactElement => (
              <Text>{customer ? customer.uuid : 'Guest'}</Text>
            ),
          },
          {
            title: 'Redeemed at',
            dataIndex: 'createdAt',
            render: (createdAt: string): string =>
              dayjs(createdAt).format(EDateFormat.DATE_TIME_SHORT),
          },
        ]}
      />

      {assignments && (
        <>
          <Divider>Assigned recipients</Divider>
          <Table
            rowKey={(record: IDiscountAssignmentAdmin): string =>
              record.customer.uuid
            }
            loading={inProcess}
            dataSource={assignments}
            pagination={false}
            columns={[
              {
                title: 'Customer',
                dataIndex: 'customer',
                render: (
                  customer: IDiscountAssignmentAdmin['customer'],
                ): string => customer.email ?? customer.uuid,
              },
              {
                title: 'Notified',
                dataIndex: 'notifiedAt',
                render: (notifiedAt: string | undefined): React.ReactElement =>
                  notifiedAt ? (
                    <Tag color="blue">
                      {dayjs(notifiedAt).format(EDateFormat.DATE_TIME_SHORT)}
                    </Tag>
                  ) : (
                    <Tag>Not yet</Tag>
                  ),
              },
              {
                title: 'Redeemed',
                dataIndex: 'redeemed',
                render: (redeemed: boolean): React.ReactElement =>
                  redeemed ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>,
              },
            ]}
          />
        </>
      )}
    </Drawer>
  );
}

export default DiscountRedemptionsDrawer;
